import { branchLastActivity } from "../../lib/branch";
import { getBranchListStatus, type ListStatus } from "../BuildStatusState";

export type AiBranchKind = "autofix" | "autotune";

export type AiBranchInput = {
  id: string;
  name: string;
  respondingAt: number | null;
  lastChatEntryAt: number | null;
  lastChatEntryType: string | null;
  awaitingAction: boolean | null;
  lastRunAt: number | null;
  createdAt: number;
};

export type AutofixAutotuneWorkflowInput = {
  id: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  autofixBranchId: string | null;
  autoImproveBranchId: string | null;
  branches?: ReadonlyArray<AiBranchInput> | null;
};

export type AiBranchRow = {
  workflowId: string;
  workflowName: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  spaceId: string;
  branchId: string;
  branchName: string;
  kind: AiBranchKind;
  status: ListStatus;
  lastActivity: number;
};

// Items needing the reviewer's eyes sort to the top; ties break by most recent.
function statusRank(status: ListStatus): number {
  switch (status) {
    case "ready":
      return 0;
    case "waiting":
      return 1;
    case "thinking":
      return 2;
    case "building":
      return 3;
    default:
      return 4;
  }
}

function compareRows(a: AiBranchRow, b: AiBranchRow): number {
  return (
    statusRank(a.status) - statusRank(b.status) ||
    b.lastActivity - a.lastActivity
  );
}

export function computeAiBranchRows(
  workflows: ReadonlyArray<AutofixAutotuneWorkflowInput>
): { autofix: AiBranchRow[]; autotune: AiBranchRow[] } {
  const autofix: AiBranchRow[] = [];
  const autotune: AiBranchRow[] = [];

  for (const workflow of workflows) {
    const branches = workflow.branches ?? [];
    const build = (
      branchId: string,
      kind: AiBranchKind
    ): AiBranchRow | null => {
      const branch = branches.find((b) => b.id === branchId);
      if (!branch) return null;
      return {
        workflowId: workflow.id,
        workflowName: workflow.name,
        badgeSeed: workflow.badgeSeed,
        pausedAt: workflow.pausedAt,
        spaceId: workflow.spaceId,
        branchId: branch.id,
        branchName: branch.name,
        kind,
        status: getBranchListStatus(branch),
        lastActivity: branchLastActivity(branch),
      };
    };

    if (workflow.autofixBranchId) {
      const row = build(workflow.autofixBranchId, "autofix");
      if (row) autofix.push(row);
    }
    if (workflow.autoImproveBranchId) {
      const row = build(workflow.autoImproveBranchId, "autotune");
      if (row) autotune.push(row);
    }
  }

  autofix.sort(compareRows);
  autotune.sort(compareRows);
  return { autofix, autotune };
}
